import {
  callApexRest,
  registerUpload
} from '../library/aws'

import {
  bytesConvertor,
  timeConvertor,
  isEmail,
  equalsIgnoreOrder
} from '../library/general'

import {
  UPDATE_USER_ATTRIBUTES
} from './../stores/auth'

const initialState = {
  fetched: false,
  uploads: {},
  recentFiles: [],
  relatedAccounts: {},
  accessToMultipleS3Uploaders: false,
  accessToAtleastOneS3Uploader: false,
  uploadBucket: null,
  uploadAccounts: [],
  selectedUploadAccount: {},
  uploadEmailNotificationsList: [],
  uploadEmailNotificationsLoading: false,
  uploadEmailNotificationsHasError: false,
  uploadEmailNotificationsError: '',
  loading: false,
  error: '',
  hasError: false
}

const RESET_STATE = 'uploads/RESET_STATE'
const UPDATE = 'uploads/UPDATE'
const ADD_UPLOAD = 'uploads/ADD_UPLOAD'
const REMOVE_UPLOAD = 'uploads/REMOVE_UPLOAD'
const UPDATE_UPLOAD = 'uploads/UPDATE_UPLOAD'
const START_NEXT_IF_CAN = 'uploads/START_NEXT_IF_CAN'

export const UPLOADING = 'UPLOADING'
export const COMPLETE = 'COMPLETE'
export const FAILED = 'FAILED'
export const CANCELLED = 'CANCELLED'
export const QUEUED = 'QUEUED'

const handler = (state = initialState, action) => {
  const {payload} = action
  const {uploads} = state

  switch (action.type) {
    case RESET_STATE:
      return {
        ...initialState,
      }

    case UPDATE:
      return {
        ...state,
        ...payload
      }

    case ADD_UPLOAD:
    case UPDATE_UPLOAD:
      uploads[payload.key] = payload

      return {
        ...state,
        uploads: {
          ...uploads
        }
      }

    case REMOVE_UPLOAD:
      const uploadToDelete = payload.key
      delete uploads[uploadToDelete]

      return {
        ...state,
        uploads: {
          ...uploads
        }
      }

    case START_NEXT_IF_CAN:
      // Go down the list and see if there are any uploads that can be started
      let inProgressUploads = 0

      for (const [, value] of Object.entries(uploads)) {
        if (value.status === UPLOADING) {
          inProgressUploads++
        }
      }

      let hasSomethingToStart = false

      for (const [, value] of Object.entries(uploads)) {
        if (value.status === QUEUED) {
          // check if it can be started
          if (inProgressUploads === 0) {
            inProgressUploads++

            value.startTime = Date.now()
            value.status = UPLOADING
            value.managedUpload.tags.push({
              'Key': 'StartTime',
              'Value': new Date().getTime().toString()
            })

            value.managedUpload.send((error, data) => {
              if (error) {
                if (error.toString() !== 'RequestAbortedError: Request aborted by user') {
                  console.error(error)
                  return value.error(error)
                }
              } else {
                return value.complete()
              }
            })

            value.managedUpload.on('httpUploadProgress', data => value.progress(data.loaded))
            hasSomethingToStart = true
          }
        }
      }

      if (hasSomethingToStart) {
        return {
          ...state,
          uploads: {
            ...uploads
          }
        }
      }

      return state

    default:
      return state
  }
}

// Which related accounts this contact may upload to, and which one is pre-selected.
//
// S3 Uploader access is granted PER RELATIONSHIP (account contact role), not per contact. A contact's
// direct relationship (`isPrimary`, the Account on the Contact) may have no uploader access while
// another relationship does — e.g. John Doe belongs to Test Co (no access) but uploads for Test B Co.
// The uploader must therefore work off the ACCESSIBLE accounts only: pre-select the primary when it
// is accessible, otherwise the first accessible account. v2.0.0 regression: it pre-selected ONLY an
// accessible primary, so with an inaccessible primary nothing was selected, the dropzone stayed
// disabled, and with a single accessible account the picker was hidden — the contact had no way to
// upload at all. Pure; unit-tested in src/__tests__/uploadAccounts.test.js.
export const deriveUploadAccounts = relatedAccounts => {
  const uploadAccounts = []
  let primary = null

  Object.keys(relatedAccounts || {}).forEach(key => {
    const related = relatedAccounts[key]

    if (!related || !related.accessToS3Uploader) {
      return
    }

    const uploadAccount = {
      id: related.accountId,
      label: related.accountName,
      type: 'account',
      rootpath: related.rootpath
    }

    uploadAccounts.push(uploadAccount)

    if (related.isPrimary && !primary) {
      primary = uploadAccount
    }
  })

  const selectedUploadAccount = primary || uploadAccounts[0] || {}

  return {
    uploadAccounts,
    selectedUploadAccount,
    accessToAtleastOneS3Uploader: uploadAccounts.length > 0,
    accessToMultipleS3Uploaders: uploadAccounts.length > 1
  }
}

export const requestUploads = () => {
  return async (dispatch, getState) => {
    dispatch({
      type: UPDATE,
      payload: {
        loading: true,
        hasError: false,
        error: '',
      }
    })

    try {
      const data = await callApexRest('UPLOADS', null, null)

      const {
        files,
        bucket,
        contactFullName,
        contactEmailAddress,
        primaryAccountName,
        relatedAccounts,
        emailNotificationsList: uploadEmailNotificationsList,
        primaryPostOpsAccountManagerName,
        primaryPostOpsAccountManagerEmail,
        primaryPostOpsAccountManagerPhone,
        primaryLiveOpsAccountManagerName,
        primaryLiveOpsAccountManagerEmail,
        primaryLiveOpsAccountManagerPhone,
        primaryAccountSalesEngineerName,
        primaryAccountSalesEngineerEmail,
        primaryAccountSalesEngineerPhone,
        showInvoicesTab
      } = data

      dispatch({
        type: UPDATE_USER_ATTRIBUTES,
        payload: {
          contactName: contactFullName,
          contactEmail: contactEmailAddress,
          accountName: primaryAccountName,
          primaryPostOpsAccountManagerName,
          primaryPostOpsAccountManagerEmail,
          primaryPostOpsAccountManagerPhone,
          primaryLiveOpsAccountManagerName,
          primaryLiveOpsAccountManagerEmail,
          primaryLiveOpsAccountManagerPhone,
          primaryAccountSalesEngineerName,
          primaryAccountSalesEngineerEmail,
          primaryAccountSalesEngineerPhone,
          showInvoicesTab
        }
      })

      const derived = deriveUploadAccounts(relatedAccounts)

      // Dev-server diagnostic only (never in a deployed build): what Salesforce granted this contact.
      // Counts and flags only — no account names, no payload (AI_INSTRUCTIONS §6 confidentiality).
      if (process.env.NODE_ENV === 'development') {
        const all = Object.values(relatedAccounts || {})
        console.info(
          `[uploads] related accounts: ${all.length}, with S3 Uploader access: ${derived.uploadAccounts.length}, ` +
          `primary present: ${all.some(r => r && r.isPrimary)}, primary has access: ${all.some(r => r && r.isPrimary && r.accessToS3Uploader)}, ` +
          `pre-selected: ${derived.selectedUploadAccount.id ? 'yes' : 'none'}`
        )
      }

      dispatch({
        type: UPDATE,
        payload: {
          fetched: true,
          recentFiles: files,
          relatedAccounts,
          uploadEmailNotificationsList,
          accessToMultipleS3Uploaders: derived.accessToMultipleS3Uploaders,
          uploadBucket: bucket
        }
      })

      const {
        uploadAccounts: existingUploadAccounts
      } = getState().stores.upload

      // Only (re)compute the account list on the first fetch; a Refresh keeps the user's pick.
      if (existingUploadAccounts.length === 0) {
        dispatch({
          type: UPDATE,
          payload: {
            uploadAccounts: derived.uploadAccounts,
            accessToAtleastOneS3Uploader: derived.accessToAtleastOneS3Uploader,
            selectedUploadAccount: derived.selectedUploadAccount
          }
        })
      }
    } catch (e) {
      dispatch({
        type: UPDATE,
        payload: {
          error: e.message,
          hasError: true
        }
      })
    }

    dispatch({
      type: UPDATE,
      payload: {
        loading: false
      }
    })
  }
}

export const requestUploadAccountChange = selectedUploadAccount => {
  return async (dispatch, getState) => {
    dispatch({
      type: UPDATE,
      payload: {
        selectedUploadAccount: selectedUploadAccount[0]
      }
    })
  }
}

export const requestHandleNewFiles = filesArray => {
  return async (dispatch, getState) => {
    let {
      uploads,
      selectedUploadAccount,
      uploadBucket
    } = getState().stores.upload

    const {
      contactId,
      aberdeenImpersonator
    } = getState().stores.auth

    Array.from(filesArray).forEach(file => {
      const {name, size} = file

      let fileAlreadyUploading = false

      for (const [, value] of Object.entries(uploads)) {
        if (value.name === name) {
          fileAlreadyUploading = true
          break
        }
      }

      const key = selectedUploadAccount.rootpath + name

      // an existing file with the same name was not found
      if (!fileAlreadyUploading) {
        let metadata = [{
          name: 'uploadmethod',
          value: 'S3 Uploader'
        }]

        let tags = [{
          name: 'UploadMethod',
          value: 'S3 Uploader'
        }]

        if (!aberdeenImpersonator) {
          metadata.push({
            name: 'uploadedbycustomercontact',
            value: contactId
          })

          tags.push({
            name: 'UploadedByCustomerContact',
            value: contactId
          })
        }

        let upload = {
          name,
          payload: file,
          key,
          status: QUEUED,
          uploadedBytes: 0,
          totalBytes: size,
          uploadedBytesFriendly: bytesConvertor(0),
          totalBytesFriendly: bytesConvertor(size),
          percentage: 0,
          bucket: uploadBucket,
          metadata,
          tags
        }

        upload.managedUpload = registerUpload(upload)
        upload.status = QUEUED

        upload.abortUpload = () => {
          if (upload.status === UPLOADING || upload.status === QUEUED) {
            if (upload.status === UPLOADING) {
              upload.managedUpload.abort()
            }

            upload.status = CANCELLED
            upload.percentage = 100

            dispatch({
              type: REMOVE_UPLOAD,
              payload: upload
            })

            dispatch({
              type: START_NEXT_IF_CAN
            })
          }
        }

        upload.complete = () => {
          upload.status = COMPLETE
          upload.uploadTime = timeConvertor(Date.now() - upload.startTime)

          dispatch({
            type: UPDATE_UPLOAD,
            payload: upload
          })

          dispatch({
            type: START_NEXT_IF_CAN
          })

          checkSendEmailNotification(dispatch, getState)
        }

        upload.error = error => {
          upload.status = FAILED
          upload.errorMessage = `Upload failed: ${error.name}`
          upload.percentage = 100

          dispatch({
            type: UPDATE_UPLOAD,
            payload: upload
          })

          dispatch({
            type: START_NEXT_IF_CAN
          })

          checkSendEmailNotification(dispatch, getState)
        }

        upload.progress = uploadedBytes => {
          upload.uploadedBytes = uploadedBytes
          upload.percentage = parseFloat((uploadedBytes / upload.totalBytes * 100).toFixed(1))

          upload.uploadedBytesFriendly = bytesConvertor(uploadedBytes)

          // calculate remaining time
          upload.timeLeftForUpload = (upload.totalBytes - uploadedBytes) * ((Date.now() - upload.startTime) / uploadedBytes)
          upload.timeLeftForUploadFriendly = timeConvertor(upload.timeLeftForUpload)

          dispatch({
            type: UPDATE_UPLOAD,
            payload: upload
          })
        }

        dispatch({
          type: ADD_UPLOAD,
          payload: upload
        })

        dispatch({
          type: START_NEXT_IF_CAN
        })
      }
    })
  }
}

const checkSendEmailNotification = (dispatch, getState) => {
  let {
    uploads,
    uploadEmailNotificationsList
  } = getState().stores.upload

  if (uploadEmailNotificationsList && uploadEmailNotificationsList.length > 0) {
    // Check if there are other uploads ongoing,
    // if not send an email notification with completed and error uploads
    let completedUploads = []
    let failedUploads = []
    let inProgressUploads = []

    for (const [, value] of Object.entries(uploads)) {
      if (value.status === UPLOADING || value.status === QUEUED) {
        inProgressUploads.push(value.name)
      }

      if (value.status === COMPLETE) {
        completedUploads.push(value.name)
      }

      if (value.status === FAILED) {
        failedUploads.push(value.name)
      }
    }

    if (inProgressUploads.length === 0) {
      if (completedUploads.length > 0 || failedUploads.length > 0) {
        callApexRest('UPLOAD_EMAIL', {completedUploads, failedUploads}, null)
          .catch(e => console.error('[UPLOAD_EMAIL]', e))
      }
    }
  }
}

// emailsList is a string[] — the component manages chip state locally,
// passing the final array here for validation and API persistence.
export const requestUpdateNotifications = emailsList => {
  return async (dispatch, getState) => {
    try {
      const cleanList = emailsList.map(e => e.trim()).filter(Boolean)

      // `|| []` (not a destructure default) so a null list — not just undefined
      // — is coerced to an array before equalsIgnoreOrder reads .length on it.
      const current = getState().stores.upload.uploadEmailNotificationsList || []

      if (equalsIgnoreOrder(cleanList, current)) {
        return true
      }

      dispatch({
        type: UPDATE,
        payload: {
          uploadEmailNotificationsHasError: false,
          uploadEmailNotificationsError: '',
          uploadEmailNotificationsLoading: true
        }
      })

      const allValid = cleanList.length === 0 || cleanList.every(e => isEmail(e))

      if (!allValid) {
        dispatch({
          type: UPDATE,
          payload: {
            uploadEmailNotificationsLoading: false,
            uploadEmailNotificationsHasError: true,
            uploadEmailNotificationsError: 'One or more email addresses are invalid.'
          }
        })
        return false
      }

      await callApexRest('UPDATE_CONTACT_LIST', { emailsList: cleanList }, null)

      dispatch({
        type: UPDATE,
        payload: {
          uploadEmailNotificationsList: cleanList,
          uploadEmailNotificationsLoading: false
        }
      })

      return true
    } catch (e) {
      dispatch({
        type: UPDATE,
        payload: {
          uploadEmailNotificationsLoading: false,
          uploadEmailNotificationsHasError: true,
          uploadEmailNotificationsError: e.message
        }
      })
      return false
    }
  }
}

export default handler
